import { useState, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ClipboardList, Calendar, Clock, CheckCircle2, Upload,
  FileText, Loader2, AlertTriangle, Star, MessageSquare, Download, Image as ImageIcon, Camera,
  Mic, MicOff
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { format, isPast } from "date-fns";
import { ar } from "date-fns/locale";
import { getSignedFileUrl } from "@/lib/storage-utils";
import { parseFileUrls, serializeFileUrls } from "@/lib/file-utils";
import { motion, AnimatePresence } from "framer-motion";
import MultiFileUploader from "@/components/shared/MultiFileUploader";

interface Props {
  childIds: string[];
  classIds: (string | null)[];
  nurseryId: string;
}

const encourageMessages = [
  "شاطر جدًا 👏🌟",
  "ممتاز! أحسنت 🎉",
  "برافو عليك 💪⭐",
  "رائع! استمر 🚀",
  "يا بطل! 🏆",
];

const statusConfig: Record<string, { label: string; color: string; icon: any; bg: string }> = {
  pending: { label: "لم يُسلّم", color: "text-amber-700", icon: Clock, bg: "bg-amber-50 border-amber-200" },
  submitted: { label: "تم التسليم ✅", color: "text-blue-700", icon: FileText, bg: "bg-blue-50 border-blue-200" },
  graded: { label: "تم التصحيح ⭐", color: "text-emerald-700", icon: CheckCircle2, bg: "bg-emerald-50 border-emerald-200" },
};

declare global {
  interface Window {
    webkitSpeechRecognition: any;
    SpeechRecognition: any;
  }
}

export default function ParentAssignments({ childIds, classIds, nurseryId }: Props) {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [submitChildId, setSubmitChildId] = useState<string>("");
  const [textAnswer, setTextAnswer] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  const validClassIds = classIds.filter(Boolean) as string[];

  const startVoiceAnswer = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast({ title: "⚠️ متصفحك لا يدعم التعرف على الكلام", description: "يرجى استخدام Chrome", variant: "destructive" });
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "ar-SA";
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setTextAnswer(prev => prev ? prev + " " + transcript : transcript);
      setIsListening(false);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  const stopVoiceAnswer = () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
    }
    setIsListening(false);
  };

  const { data: assignments } = useQuery({
    queryKey: ["parent-assignments", validClassIds],
    queryFn: async () => {
      if (!validClassIds.length) return [];
      const { data } = await supabase
        .from("assignments")
        .select("*, classes(name), lessons(title)")
        .in("class_id", validClassIds)
        .order("due_date", { ascending: false });
      return data || [];
    },
    enabled: validClassIds.length > 0,
  });

  const { data: submissions, refetch: refetchSubs } = useQuery({
    queryKey: ["parent-submissions", childIds],
    queryFn: async () => {
      if (!childIds.length) return [];
      const { data } = await supabase
        .from("assignment_submissions")
        .select("*")
        .in("child_id", childIds);
      return data || [];
    },
    enabled: childIds.length > 0,
  });

  const getSubmission = (assignmentId: string, childId: string) =>
    (submissions || []).find((s: any) => s.assignment_id === assignmentId && s.child_id === childId);

  const handleSubmit = async () => {
    if (!submitting || !submitChildId || !user) return;
    if (files.length === 0 && !textAnswer.trim()) {
      toast({ title: "يرجى رفع ملف أو كتابة إجابة", variant: "destructive" });
      return;
    }
    setLoading(true);
    setUploadProgress(0);

    const uploadedPaths: string[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const ext = file.name.split(".").pop();
        const path = `${nurseryId}/submissions/${submitChildId}/${Date.now()}_${i}.${ext}`;
        const { error: uploadError } = await supabase.storage.from("homework-files").upload(path, file);
        if (uploadError) throw new Error(`خطأ في رفع ${file.name}: ${uploadError.message}`);
        uploadedPaths.push(path);
        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }

      const { error } = await supabase.from("assignment_submissions").insert({
        assignment_id: submitting,
        child_id: submitChildId,
        submitted_by: user.id,
        file_url: serializeFileUrls(uploadedPaths),
        text_answer: textAnswer.trim() || null,
        status: "submitted",
        submitted_at: new Date().toISOString(),
      });

      if (error) throw error;

      setSubmitting(null);
      setTextAnswer("");
      setFiles([]);
      setUploadProgress(0);
      setSuccessMsg(encourageMessages[Math.floor(Math.random() * encourageMessages.length)]);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      refetchSubs();
    } catch (e: any) {
      toast({ title: "خطأ", description: e.message, variant: "destructive" });
    }
    setLoading(false);
  };

  const openFile = async (url: string) => {
    const signed = await getSignedFileUrl("homework-files", url);
    if (signed) window.open(signed, "_blank");
  };

  const openFiles = async (fileUrl: string) => {
    const urls = parseFileUrls(fileUrl);
    for (const url of urls) {
      const signed = await getSignedFileUrl("homework-files", url);
      if (signed) window.open(signed, "_blank");
    }
  };

  const formatDate = (d: string | null) => {
    if (!d) return "-";
    try { return format(new Date(d), "d MMM yyyy", { locale: ar }); } catch { return d; }
  };

  if (!assignments || assignments.length === 0) {
    return (
      <Card className="border-2 border-dashed border-muted">
        <CardContent className="py-16 text-center">
          <div className="text-6xl mb-4">📒</div>
          <p className="text-muted-foreground text-lg font-medium">لا توجد واجبات حالياً</p>
          <p className="text-muted-foreground text-sm mt-1">سيتم إشعارك عند إضافة واجب جديد</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Success Overlay */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          >
            <motion.div
              initial={{ y: 30 }}
              animate={{ y: 0 }}
              className="bg-background rounded-3xl p-10 text-center shadow-2xl max-w-sm mx-4"
            >
              <div className="text-7xl mb-4">🌟</div>
              <h2 className="text-2xl font-bold text-foreground mb-2">{successMsg}</h2>
              <p className="text-muted-foreground">تم تسليم الواجب بنجاح</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ScrollArea className="max-h-[600px]">
        <div className="space-y-4">
          {assignments.map((a: any) => {
            const overdue = isPast(new Date(a.due_date));
            return childIds.map((childId) => {
              const sub = getSubmission(a.id, childId);
              const st = sub ? statusConfig[sub.status] || statusConfig.pending : statusConfig.pending;
              const assignmentFiles = parseFileUrls(a.file_url);
              const submissionFiles = sub ? parseFileUrls(sub.file_url) : [];

              return (
                <motion.div
                  key={`${a.id}-${childId}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className={`overflow-hidden border-2 ${sub ? st.bg : "border-muted"}`}>
                    <CardContent className="p-0">
                      <div className={`px-5 py-3 flex items-center gap-3 ${
                        sub?.status === "graded" ? "bg-emerald-100 dark:bg-emerald-900/20" : 
                        sub?.status === "submitted" ? "bg-blue-100 dark:bg-blue-900/20" : 
                        "bg-amber-50 dark:bg-amber-900/10"
                      }`}>
                        <span className="text-3xl">{sub?.status === "graded" ? "⭐" : sub?.status === "submitted" ? "✅" : "📒"}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-foreground text-base">{a.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {a.classes?.name}{a.lessons?.title && ` • ${a.lessons.title}`}
                          </p>
                        </div>
                        <Badge className={`text-xs shrink-0 ${st.color} bg-background/80`}>
                          {st.label}
                        </Badge>
                      </div>

                      <div className="px-5 py-4 space-y-3">
                        {a.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">{a.description}</p>
                        )}

                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(a.due_date)}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Star className="w-3.5 h-3.5 text-yellow-500" />
                            {a.max_score} درجة
                          </span>
                          {overdue && !sub && (
                            <span className="flex items-center gap-1.5 text-destructive font-medium">
                              <AlertTriangle className="w-3.5 h-3.5" />
                              انتهى الموعد
                            </span>
                          )}
                        </div>

                        {/* Graded result */}
                        {sub?.status === "graded" && (
                          <div className="rounded-xl bg-gradient-to-l from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 p-4 border border-emerald-200 dark:border-emerald-800">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-2xl">🏆</span>
                                <span className="text-lg font-bold text-emerald-700 dark:text-emerald-400">
                                  {sub.score}/{a.max_score}
                                </span>
                              </div>
                              <Badge className="bg-emerald-500 text-white text-sm px-3">
                                {Math.round(((sub.score || 0) / a.max_score) * 100)}%
                              </Badge>
                            </div>
                            {sub.teacher_notes && (
                              <div className="flex items-start gap-2 mt-3 text-sm text-emerald-700 dark:text-emerald-400 bg-white/50 dark:bg-white/5 rounded-lg p-2">
                                <MessageSquare className="w-4 h-4 mt-0.5 shrink-0" />
                                <span>{sub.teacher_notes}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {assignmentFiles.length > 0 && (
                            <Button variant="outline" size="sm" onClick={() => openFiles(a.file_url)} className="rounded-full">
                              <Download className="w-4 h-4 ml-1" />
                              ملفات الواجب ({assignmentFiles.length})
                            </Button>
                          )}
                          {submissionFiles.length > 0 && (
                            <Button variant="outline" size="sm" onClick={() => openFiles(sub.file_url)} className="rounded-full">
                              <ImageIcon className="w-4 h-4 ml-1" />
                              ملفات الحل ({submissionFiles.length})
                            </Button>
                          )}
                          {!sub && (
                            <Button
                              size="lg"
                              className="mr-auto rounded-full text-base gap-2 px-6 bg-gradient-to-l from-primary to-primary/80 shadow-lg hover:shadow-xl transition-all"
                              onClick={() => {
                                setSubmitting(a.id);
                                setSubmitChildId(childId);
                              }}
                            >
                              <Camera className="w-5 h-5" />
                              📷 رفع صور الحل
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            });
          })}
        </div>
      </ScrollArea>

      {/* Submit Dialog - Multi-file */}
      <Dialog open={!!submitting} onOpenChange={(open) => { if (!open) { setSubmitting(null); setFiles([]); setUploadProgress(0); } }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <span className="text-2xl">📒</span>
              تسليم الواجب
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {/* Multi-file uploader */}
            <MultiFileUploader
              files={files}
              onFilesChange={setFiles}
              accept="image/*,.pdf"
              label="📸 صوّر الكراسة أو اسحب الصور هنا"
              capture
            />

            <div className="space-y-2">
              <Label className="text-sm">إجابة نصية (اختياري)</Label>
              <Textarea
                value={textAnswer}
                onChange={(e) => setTextAnswer(e.target.value)}
                placeholder="اكتب الإجابة هنا أو استخدم الميكروفون..."
                rows={2}
                className="rounded-xl"
              />
              <Button
                type="button"
                variant={isListening ? "destructive" : "outline"}
                size="sm"
                className={`w-full gap-2 rounded-full ${isListening ? "animate-pulse" : ""}`}
                onClick={() => isListening ? stopVoiceAnswer() : startVoiceAnswer()}
              >
                {isListening ? (
                  <>
                    <MicOff className="w-4 h-4" />
                    إيقاف التسجيل...
                  </>
                ) : (
                  <>
                    <Mic className="w-4 h-4" />
                    🎤 جاوب بالصوت
                  </>
                )}
              </Button>
            </div>

            {loading && uploadProgress > 0 && uploadProgress < 100 && (
              <div className="space-y-1">
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground text-center">جاري الرفع... {uploadProgress}%</p>
              </div>
            )}

            <Button
              className="w-full h-12 text-base rounded-full bg-gradient-to-l from-primary to-primary/80 shadow-lg"
              onClick={handleSubmit}
              disabled={loading || (files.length === 0 && !textAnswer.trim())}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 ml-2 animate-spin" />
              ) : (
                <Upload className="w-5 h-5 ml-2" />
              )}
              تسليم الواجب 🚀
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
